import { createContext, useEffect, useState, ReactNode } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { setCredentials, logout as logoutAction } from '../store/slices/authSlice';
import { authService } from '@/lib/api/authService';
import { type Role } from '@/types/auth.types';
import { type User, UserRole } from '@/types';

const TOKEN_KEY = 'pfg_token';
const USER_KEY = 'pfg_user';

function mapApiRoleToUserRole(apiRole: Role): UserRole {
  switch (apiRole) {
    case 'CLUB_ADMIN':       return UserRole.CLUB;
    case 'NGO_ADMIN':        return UserRole.NGO
    case 'INDIVIDUAL_PLAYER': return UserRole.PLAYER;
    case 'SUPER_ADMIN':      return UserRole.ADMIN;
  }
}

export interface AuthContextType {
  user: (User & { isVerified?: boolean }) | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string, role: Role) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<(User & { isVerified?: boolean }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);
    if (token && storedUser) {
      try {
        const parsed = JSON.parse(storedUser) as User & { isVerified?: boolean };
        setUser(parsed);
        dispatch(setCredentials({ user: parsed, token }));
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    setLoading(false);
  }, [dispatch]);

  async function login(email: string, password: string, role: Role): Promise<void> {
    setError(null);
    const response = await authService.login({ email, password, role });
    const { accessToken, user: apiUser } = response;

    const mappedUser: User & { isVerified?: boolean } = {
      id: apiUser.id,
      email: apiUser.email,
      fullName: apiUser.profile?.name || 'User',
      name: apiUser.profile?.name || 'User',
      emailVerified: true,
      role: mapApiRoleToUserRole(apiUser.role),
      status: (apiUser.profile?.status?.toLowerCase() as User['status']) || 'pending',
      isVerified: apiUser.isVerified ?? (apiUser.profile?.status === 'ACTIVE'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(mappedUser));
    authService.setStoredToken(accessToken);
    setUser(mappedUser);
    dispatch(setCredentials({ user: mappedUser, token: accessToken }));
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    authService.removeStoredToken();
    setUser(null);
    dispatch(logoutAction());
  }

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
